import { useMemo } from "react";
import { useAvailabilitySheet } from "./store";
import { Calendar as CalendarIcon, Clock, X } from "lucide-react";
import { DateTime } from "luxon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import AvailabilityDay from "./availability-day";
import { cn } from "@/lib/utils";
import { lookupHumanIana } from "@/lib/timezone";
import dateStringToLocalDate from "@/utils/dateStringToLocalDate";

const formatTime = (time: string, zone: string) =>
  DateTime.fromISO(`1970-01-01T${time}`, { zone }).toFormat("h:mm a");

const SpecificAvailability = ({ duration }: { duration?: number }) => {
  const entries = useAvailabilitySheet((s) => s.specificAvailabilities);
  const setEntries = useAvailabilitySheet((s) => s.setSpecificAvailabilities);
  const timezone = useAvailabilitySheet((s) => s.timezone);

  const sortedEntries = useMemo(() => {
    const newEntries = entries.reduce(
      (acc, curr) => {
        curr.dates.forEach((date) => {
          const i = acc.findIndex((e) => e.date === date);
          if (i < 0) {
            acc.push({
              date,
              times: [...curr.times],
            });
          } else {
            acc[i]?.times.push(...curr.times);
          }
        });

        return acc;
      },
      [] as { date: string; times: { startTime: string; endTime: string }[] }[],
    );

    newEntries.sort((a, b) => a.date.localeCompare(b.date));
    newEntries.forEach((entry) =>
      entry.times.sort((a, b) => a.startTime.localeCompare(b.startTime)),
    );

    return newEntries;
  }, [entries]);

  const deleteEntry = (date: string) => {
    const newEntries = entries
      // Remove date from all entries
      .map((entry) => ({
        ...entry,
        dates: entry.dates.filter((oldDate) => oldDate !== date),
      }))
      // Filter out any entries with no dates
      .filter(({ dates }) => dates.length);

    setEntries(newEntries);
  };

  return (
    <div>
      <div className="pt-8 pb-4">
        <div className="flex items-center gap-1">
          <CalendarIcon className="size-4" />
          <p className="font-semibold leading-none text-sm">
            Specific Dates & Times
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          {duration
            ? `What times are you available for a ${duration} minute interview?`
            : "What times are you available for an interview?"}
        </p>
        <p className="text-sm text-muted-foreground">
          Choose hours on specific days
        </p>
      </div>
      <div
        className={cn("space-y-2", {
          "pb-4": entries.length > 0,
        })}
      >
        {sortedEntries.map((e, i) => (
          <div
            key={`specific-availability-${i}`}
            className="flex items-center justify-between w-full border border-border rounded-md p-3"
          >
            <div className="space-y-1">
              <p className="text-wrap font-semibold leading-none text-sm">
                {dateStringToLocalDate(e.date)?.toLocaleDateString()}
              </p>

              {e.times.map((t, j) => (
                <div
                  key={`specific-availability-time-${i}-${j}`}
                  className="flex items-center gap-2 text-muted-foreground"
                >
                  <Clock className="size-4" />
                  <p className="text-sm leading-none">
                    {formatTime(t.startTime, timezone)} -{" "}
                    {formatTime(t.endTime, timezone)}
                  </p>
                </div>
              ))}
            </div>

            <X
              className="size-4 min-w-4 hover:cursor-pointer hover:opacity-80"
              onClick={() => deleteEntry(e.date)}
            />
          </div>
        ))}
      </div>
      <SpecificAvailabilityEntry />
    </div>
  );
};

export default SpecificAvailability;

const SpecificAvailabilityEntry = () => {
  const open = useAvailabilitySheet((s) => s.specificOpen);
  const setOpen = useAvailabilitySheet((s) => s.setSpecificOpen);
  const setForm = useAvailabilitySheet((s) => s.setSpecificAvailabilitiesForm);
  const submit = useAvailabilitySheet((s) => s.confirmSpecificAvailabilities);
  const { dates, times } = useAvailabilitySheet(
    (s) => s.specificAvailabilitiesForm,
  );

  const canSubmit =
    dates.length &&
    times.length > 0 &&
    times.every((time) => time.startTime && time.endTime && !time.placeholder);

  const ianaTimezone = times[0]?.timezone;
  const readableTimezone = ianaTimezone && lookupHumanIana(ianaTimezone);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={"outline"}>Add Dates & Times</Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="left"
        sideOffset={16}
        className="w-auto p-0 flex flex-col items-center grow-0"
      >
        <Calendar
          className="h-full w-full flex"
          classNames={{
            months:
              "flex w-full flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 flex-1",
            month: "space-y-4 w-full flex flex-col",
            table: "w-full h-full border-collapse space-y-1",
            head_row: "",
            row: "w-full mt-2",
            cell: "[&:has([aria-selected].day-outside)]:bg-background",
          }}
          mode="multiple"
          selected={dates
            .map(dateStringToLocalDate)
            .filter((v): v is Date => Boolean(v))}
          onSelect={(dates) => {
            setForm((old) => ({
              ...old,
              // Only extract the date part of the date object
              dates:
                dates?.map((date) =>
                  new Date(date).toLocaleDateString("en-CA"),
                ) ?? [],
            }));
          }}
        />
        <div className="p-4 border-t border-border w-full">
          {readableTimezone && (
            <p className="pb-2 text-muted-foreground leading-none text-sm">
              {readableTimezone}
            </p>
          )}
          <AvailabilityDay
            entries={times}
            setEntries={(entry) => setForm((f) => ({ ...f, times: entry }))}
          />
          <Button
            className="mt-4 w-full"
            variant="secondary"
            disabled={!canSubmit}
            onClick={() => submit()}
          >
            Add To Schedule
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
